/**
 * The fishing minigame — US 20.01, spec 110 §2. Side-on water column on a
 * DOM canvas: the hook rides its line at x=0.5 and moves vertically
 * (ArrowUp/Down, W/S, or touch drag); fish swim horizontally per the
 * SERVER'S schedule, positioned by the same shared fishX the validator
 * replays. Contact = catch, 1s reset, basket accumulates; on exit the
 * whole haul submits once, with ONE overflow prompt if it doesn't fit.
 */
import {
  CATCH_DEPTH_TOLERANCE,
  CATCH_X_TOLERANCE,
  fishX,
  HOOK_RESET_MS,
  HOOK_SPEED,
  HOOK_X,
  type CatchClaim,
  type FishSpawn,
} from '@game/fishing.ts';
import { worldBus } from '@shared/bus.js';
import { newActionId } from '@shared/envelope.js';
import { callDialogue } from '@transport/api.js';
import { button, toast } from './design/components.js';
import { showOverflowPrompt } from './prompts.js';

const W = 640;
const H = 400;
const SURFACE_Y = 40;
const FLOOR_Y = H - 20;

interface StartResult {
  sessionId: string;
  durationMs: number;
  schedule: FishSpawn[];
}

interface SubmitResult {
  caught: { itemId: string; qty: number }[];
  state: unknown;
}

export function initFishingPanel(root: HTMLElement, onStateChanged: () => void): void {
  worldBus.on('fishing-requested', ({ spotId }) => {
    void callDialogue<StartResult>({ op: 'fishing_start', spotId }).then((r) => {
      if (!r.ok) {
        toast(root, r.message || 'Cannot fish here', 'danger');
        return;
      }
      run(r.result);
    });
  });

  function run(session: StartResult): void {
    worldBus.emit('modal-input', { captured: true });
    const overlay = document.createElement('div');
    overlay.className = 'wca-modal-backdrop';
    overlay.style.pointerEvents = 'auto';
    const box = document.createElement('div');
    box.className = 'wca-modal wca-fishing';
    const head = document.createElement('div');
    head.className = 'wca-dialogue-head';
    const title = document.createElement('span');
    title.textContent = 'Fishing';
    const status = document.createElement('span');
    head.append(title, status);
    const canvas = document.createElement('canvas');
    canvas.width = W;
    canvas.height = H;
    canvas.className = 'wca-fishing-canvas';
    const ctx = canvas.getContext('2d')!;
    box.append(head, canvas);
    overlay.appendChild(box);
    root.appendChild(overlay);

    const startedAt = performance.now();
    const claims: CatchClaim[] = [];
    const caughtIndices = new Set<number>();
    let hookDepth = 0; // 0..1
    let hookDir = 0;
    let resetUntil = 0; // performance.now() timestamp while resetting
    let finished = false;

    const depthToY = (d: number) => SURFACE_Y + d * (FLOOR_Y - SURFACE_Y);

    // ---- input: keys + touch drag (12.01 spirit, modal-scoped) ----------
    const onKey = (down: boolean) => (ev: KeyboardEvent) => {
      if (ev.code === 'ArrowUp' || ev.code === 'KeyW') {
        hookDir = down ? -1 : hookDir === -1 ? 0 : hookDir;
        ev.preventDefault();
      }
      if (ev.code === 'ArrowDown' || ev.code === 'KeyS') {
        hookDir = down ? 1 : hookDir === 1 ? 0 : hookDir;
        ev.preventDefault();
      }
      if (down && ev.code === 'Escape') void finish();
    };
    const keyDown = onKey(true);
    const keyUp = onKey(false);
    window.addEventListener('keydown', keyDown);
    window.addEventListener('keyup', keyUp);
    let touchY: number | null = null;
    canvas.addEventListener('pointerdown', (ev) => {
      touchY = ev.clientY;
      canvas.setPointerCapture(ev.pointerId);
    });
    canvas.addEventListener('pointermove', (ev) => {
      if (touchY === null) return;
      hookDir = ev.clientY > touchY + 6 ? 1 : ev.clientY < touchY - 6 ? -1 : 0;
    });
    canvas.addEventListener('pointerup', () => {
      touchY = null;
      hookDir = 0;
    });

    let lastFrame = startedAt;
    function frame(now: number): void {
      if (finished) return;
      const dt = Math.min(0.05, (now - lastFrame) / 1000);
      lastFrame = now;
      const t = now - startedAt;
      if (t >= session.durationMs) {
        void finish();
        return;
      }

      // Hook: frozen at the surface during the reset, else player-driven.
      if (now < resetUntil) {
        hookDepth = 0;
      } else {
        hookDepth = Math.max(0, Math.min(1, hookDepth + hookDir * HOOK_SPEED * dt));
      }

      // Catch detection — the same arithmetic the server replays (20.03).
      if (now >= resetUntil) {
        for (const f of session.schedule) {
          if (caughtIndices.has(f.index)) continue;
          const x = fishX(f, t);
          if (x === null) continue;
          if (
            Math.abs(x - HOOK_X) <= CATCH_X_TOLERANCE &&
            Math.abs(hookDepth - f.depth) <= CATCH_DEPTH_TOLERANCE
          ) {
            caughtIndices.add(f.index);
            claims.push({ fishIndex: f.index, catchTimeMs: t, hookDepth });
            resetUntil = now + HOOK_RESET_MS;
            break;
          }
        }
      }

      // ---- draw -----------------------------------------------------------
      ctx.fillStyle = '#0d2b45';
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#1976d2';
      ctx.fillRect(0, SURFACE_Y, W, FLOOR_Y - SURFACE_Y);
      ctx.fillStyle = '#8d6e63';
      ctx.fillRect(0, FLOOR_Y, W, H - FLOOR_Y);

      for (const f of session.schedule) {
        if (caughtIndices.has(f.index)) continue;
        const x = fishX(f, t);
        if (x === null) continue;
        const fy = depthToY(f.depth);
        ctx.fillStyle = f.depth > 0.5 ? '#ffb300' : '#cfd8dc';
        ctx.beginPath();
        ctx.ellipse(x * W, fy, 14, 7, 0, 0, Math.PI * 2);
        ctx.fill();
        // Tail on the trailing side shows direction.
        ctx.fillRect(x * W + (f.speed > 0 ? -20 : 12), fy - 4, 8, 8);
      }

      const hx = HOOK_X * W;
      const hy = depthToY(hookDepth);
      ctx.strokeStyle = '#f5f5f5';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(hx, 0);
      ctx.lineTo(hx, hy);
      ctx.stroke();
      ctx.fillStyle = now < resetUntil ? '#58c470' : '#ffd54f';
      ctx.beginPath();
      ctx.arc(hx, hy, 6, 0, Math.PI * 2);
      ctx.fill();

      status.textContent = `🐟 ${claims.length}   ⏱ ${Math.ceil((session.durationMs - t) / 1000)}s — Esc to reel in`;
      requestAnimationFrame(frame);
    }
    requestAnimationFrame(frame);

    async function finish(): Promise<void> {
      if (finished) return;
      finished = true;
      window.removeEventListener('keydown', keyDown);
      window.removeEventListener('keyup', keyUp);
      // One actionId across the overflow re-send (106 §2).
      const actionId = newActionId();
      let res = await callDialogue<SubmitResult>(
        { op: 'fishing_submit', sessionId: session.sessionId, catches: claims },
        { actionId },
      );
      if (!res.ok && res.error === 'INVENTORY_FULL' && res.manifest) {
        const mode = await showOverflowPrompt(root, res.manifest, (id) => id);
        if (mode) {
          res = await callDialogue<SubmitResult>(
            {
              op: 'fishing_submit',
              sessionId: session.sessionId,
              catches: claims,
              confirmOverflow: mode,
            },
            { actionId },
          );
        }
      }
      overlay.remove();
      worldBus.emit('modal-input', { captured: false });
      if (res.ok) {
        const total = res.result.caught.reduce((n, c) => n + c.qty, 0);
        toast(root, total > 0 ? `Reeled in ${total} fish.` : 'Nothing biting today.');
        onStateChanged();
      } else if (res.error === 'SESSION_VOID') {
        toast(root, 'That session did not check out.', 'danger');
      } else if (res.message) {
        toast(root, res.message, 'danger');
      }
    }

    const exit = button('Reel in & exit', () => void finish(), 'primary');
    box.appendChild(exit);
  }
}
